/**
 * Die Anzeigeregeln der Seite "Aktivitaeten" (Phase 2, Task 11B) als reine Funktionen - die Tabelle zeichnet nur, was
 * hier herauskommt:
 *
 * - `woText`: der Text der Spalte "Wo". Bestellungen wie in Phase 1; jedes andere Objekt als "<Art-Label> <Bezeichnung>".
 *   Beginnt die Bezeichnung als eigenes Wort schon mit dem Art-Label, entfaellt das vorangestellte ("FX-Konto USD", nicht
 *   "FX-Konto FX-Konto USD", Ruling 68; der CSV-Export im Backend, `orderHistory.ts#woText`, haelt dieselbe Regel).
 * - `klickZiel`: wohin ein Klick auf die Zeile fuehrt. Eine oeffenbare Bestellung (oder Sendung) in den Drawer; ein Nutzer in
 *   die Nutzerverwaltung (mit `manage_users`), ein Lieferant auf die Lieferantenseite, eine Zahlungsvorlage auf die Seite
 *   Zahlungsvorlagen mit der Vorlage als Auswahl (`objekt_id` ist die Vorlagen-Id; eine inzwischen geloeschte Vorlage nennt
 *   die Seite selbst: "gibt es nicht (mehr)"), ein FX-Objekt auf Zahlungen (mit `view_payments`); alles Uebrige ist nicht
 *   anklickbar. Das Recht prueft die Seite selbst, damit nie ein Knopf zu einer Seite fuehrt, die der Rueckfall sofort
 *   wieder verlaesst.
 * - `aenderungsAnzeige`: die lesbare Form von Vorher/Nachher. Den Text der Werte (`altAnzeige`/`neuAnzeige`) leitet der Server
 *   ab; hier stehen nur die Faelle, die er nicht kennen kann:
 *     * Altzeilen aus der Zeit vor Phase 2 (Ruling 39): `produktionsbild-galerie` traegt das Ereignis in `neu_wert`,
 *       `ist_zahlung_beleg` und `bestellgruppe` eine UUID. Statt Rohwert und Pfeil steht der Satz der Zeile. Neue Zeilen
 *       tragen Dateinamen bzw. Bestellnummern und zeigen sie wie jeden Wert. Das Backend liefert Bezeichnungen; eine rohe
 *       UUID in alt/neu ist ein Fehler seines Schreibwegs (z. B. `kindKennung`, Task 12). Die Seite ersetzt sie nur dort, wo
 *       ein Satz der Zeile an ihre Stelle treten kann - ohne Satz erfindet sie keinen Text.
 *     * Ein entferntes Artwork (Ticket F: `positionsdokument`, `betroffene[].ereignis = "entfernt"`, Altwert ohne Neuwert) zeigt den
 *       Satz der Zeile statt "Datei → leer" - als Entfernen lesbar, nicht wie ein Ersetzen.
 *     * Ein umgehaengtes Positionsdokument (Positionsersatz C1: `positionsdokument` oder `produktionsbild-galerie` mit
 *       `betroffene[].ereignis = "umgehaengt"`) zeigt ebenso den Satz der Zeile - er nennt beide Positionen und den Grund,
 *       "Datei → Datei" bzw. "– → Datei" taete es nicht. Die Zeile `"nicht_umgehaengt"` (Ziel hatte schon ein Artwork) traegt
 *       keine Werte und zeigt ihren Satz ueber die Freitextregel.
 *     * Rollen stehen als Schluessel in alt/neu (`product_manager`) und erscheinen als Oberflaechenbezeichnung ("Standard"),
 *       die Phase `custom` als "Eigene Aufgaben".
 *     * Ist alt gleich neu (PATCH einer zentralen Aufgabe ohne Titelaenderung schreibt "Titel → derselbe Titel", weil der
 *       Titel die Kennung ist), zeigt die Zeile nur die Kennung, ohne Pfeil - sonst saehe sie wie ein No-op aus.
 */
import type { Aktivitaet, AktivitaetWo, WoArt } from "../../api/aktivitaeten.ts";
import { hatRecht, SEITEN_RECHTE } from "../../state/berechtigungen.ts";
import { UUID_MUSTER } from "../App/bestellungsAufloesung.ts";
import { PHASE_CUSTOM_LABEL } from "../historieDarstellung.ts";
import { ROLLEN, rollenBezeichnung } from "../Users/RollenAuswahl.tsx";

/** Anzeigenamen der Objektarten (Spiegel von `OBJEKT_ART_LABELS`, `backend/src/domaene/aenderungsprotokoll.ts`). */
const ART_LABEL: Readonly<Record<Exclude<WoArt, "bestellung" | "sendung">, string>> = {
  fx_konto: "FX-Konto",
  fx_deal: "FX-Deal",
  fx_zuordnung: "FX-Zuordnung",
  zahlungsvorlage: "Zahlungsvorlage",
  lieferant: "Lieferant",
  zustaendigkeit: "Zuständigkeit",
  aufgabenvorlage: "Aufgabenvorlage",
  nutzer: "Nutzer",
  einstellung: "Einstellung",
};

/** Der Zusatz einer ueber den Hub geloeschten Bestellung (`Wo.geloescht`); das Backend haengt ihn schon an die Bezeichnung. */
const GELOESCHT = " (gelöscht)";

export function woText(wo: AktivitaetWo): string {
  if (wo.art === "sendung") return wo.bezeichnung ? `Sendung ${wo.bezeichnung}` : "Sendung";
  if (wo.art !== "bestellung") {
    const label = ART_LABEL[wo.art];
    const bezeichnung = wo.bezeichnung;
    if (!bezeichnung) return label;
    return bezeichnung === label || bezeichnung.startsWith(`${label} `) ? bezeichnung : `${label} ${bezeichnung}`;
  }
  const name = wo.bezeichnung || (wo.jtlOrderId != null ? `jtl-${wo.jtlOrderId}` : "Entwurf");
  return wo.geloescht && !name.endsWith(GELOESCHT) ? `${name}${GELOESCHT}` : name;
}

/** Die Seiten, in die ein Klick auf ein Objekt fuehrt (Kennungen wie `AktiveSeite`, `Navigation.tsx`). */
export type ZielSeite = "users" | "suppliers" | "paymentTemplates" | "payments";

export interface SeitenZiel {
  art: "seite";
  seite: ZielSeite;
  /** Nutzer-Id, JTL-Lieferantennummer bzw. Vorlagen-Id, wo die Seite ein Objekt auswaehlen kann; sonst `null`. */
  id: string | null;
}

export type KlickZiel = { art: "bestellung" } | SeitenZiel;

const seite = (ziel: ZielSeite, id: string | null = null): SeitenZiel => ({ art: "seite", seite: ziel, id });

/** `null` = nicht anklickbar. */
export function klickZiel(wo: AktivitaetWo, rechte: ReadonlySet<string>): KlickZiel | null {
  switch (wo.art) {
    case "bestellung":
    case "sendung":
      return wo.oeffenbar ? { art: "bestellung" } : null;
    case "nutzer":
      return wo.kennung && hatRecht(rechte, SEITEN_RECHTE.users) ? seite("users", wo.kennung) : null;
    case "lieferant":
      return wo.kennung ? seite("suppliers", wo.kennung) : null;
    case "zahlungsvorlage":
      return seite("paymentTemplates", wo.kennung || null);
    case "fx_konto":
    case "fx_deal":
    case "fx_zuordnung":
      return hatRecht(rechte, SEITEN_RECHTE.payments) ? seite("payments") : null;
    case "zustaendigkeit":
    case "aufgabenvorlage":
    case "einstellung":
      return null;
  }
}

/** Der Hinweis am Knopf der Wo-Spalte, je Ziel. */
export function klickHinweis(ziel: KlickZiel): string {
  if (ziel.art === "bestellung") return "Bestellung öffnen";
  switch (ziel.seite) {
    case "users":
      return "Nutzerverwaltung öffnen";
    case "suppliers":
      return "Lieferant öffnen";
    case "paymentTemplates":
      return "Zahlungsvorlagen öffnen";
    case "payments":
      return "Zahlungen öffnen";
  }
}

export type Aenderung =
  | { art: "satz"; text: string }
  | { art: "werte"; alt: string | null; neu: string | null };

/** Die Ereignisse der Galerie, wie Altzeilen sie in `neu_wert` tragen (`repository.ts`, ProduktionsbildEreignis). */
const GALERIE_EREIGNISSE: ReadonlySet<string> = new Set(["erstellt", "ersetzt", "geloescht", "wiederhergestellt"]);

/** Die Felder, unter denen das Backend "umgehaengt" schreibt (`backend/src/documents/umhaengen.ts`): Artwork bzw. Produktionsbild. */
const UMGEHAENGT_FELDER: ReadonlySet<string> = new Set(["positionsdokument", "produktionsbild-galerie"]);

/** Das Ereignis, das ein Eintrag von `betroffene` nennt (`positionsdokument`: "entfernt", "umgehaengt"); `betroffene` ist serverseitig frei geformt. */
function betroffenesEreignis(e: Aktivitaet): string | null {
  if (!Array.isArray(e.betroffene)) return null;
  for (const eintrag of e.betroffene as unknown[]) {
    const ereignis = typeof eintrag === "object" && eintrag !== null ? (eintrag as { ereignis?: unknown }).ereignis : undefined;
    if (typeof ereignis === "string") return ereignis;
  }
  return null;
}

const istRolle = (wert: string): boolean => (ROLLEN as readonly string[]).includes(wert);

/** Rolle und Phase aus ihren Schluesseln in die Bezeichnung der Oberflaeche; alles Uebrige unveraendert. */
function lesbar(e: Aktivitaet, wert: string | null): string | null {
  if (wert === null) return null;
  if (e.feld === "aufgabe_phase" && wert === "custom") return PHASE_CUSTOM_LABEL;
  if (e.wo.art === "nutzer") {
    if (e.feld === "role" && istRolle(wert)) return rollenBezeichnung(wert);
    // Die Anlagezeile nennt die Rolle am Ende: "<Anzeigename> (<Login>), <Rolle>".
    if (e.feld === "nutzer_angelegt") {
      const komma = wert.lastIndexOf(", ");
      if (komma >= 0 && istRolle(wert.slice(komma + 2))) return `${wert.slice(0, komma + 2)}${rollenBezeichnung(wert.slice(komma + 2))}`;
    }
  }
  return wert;
}

export function aenderungsAnzeige(e: Aktivitaet): Aenderung {
  const satz = e.summary?.trim() ? e.summary : null;
  // Freitextzeile: keine Werte, aber ein Satz (z. B. "Bestellung angelegt").
  if (e.altAnzeige === null && e.neuAnzeige === null) {
    return satz ? { art: "satz", text: satz } : { art: "werte", alt: null, neu: null };
  }
  // Altzeile der Produktionsbild-Galerie: das Ereignis steht in neu_wert (Ruling 39); der alte Schreibweg schrieb immer
  // einen Satz dazu ("Produktionsbild <Ereignis>"), er steht an der Stelle von Rohwert und Pfeil.
  if (satz && e.feld === "produktionsbild-galerie" && e.neuWert !== null && GALERIE_EREIGNISSE.has(e.neuWert)) {
    return { art: "satz", text: satz };
  }
  // Ticket F: ein entferntes Artwork steht unter `positionsdokument` - Datei samt Position als Altwert, KEIN Neuwert, das Ereignis
  // in `betroffene[].ereignis`. Als "Datei → leer" sähe es aus wie ein Ersetzen oder ein Leeren; der Satz der Zeile
  // ("Positionsdokument 'Artwork' entfernt: …", derselbe, den der Verlauf der Bestellung zeigt) sagt es.
  if (satz && e.feld === "positionsdokument" && betroffenesEreignis(e) === "entfernt") {
    return { art: "satz", text: satz };
  }
  // Positionsersatz C1: ein umgehaengtes Positionsdokument (Artwork unter `positionsdokument`, Produktionsbild unter
  // `produktionsbild-galerie`). "Position A: Datei → Position A': Datei" bzw. "– → Datei" sähe wie ein Ersetzen oder Anlegen aus;
  // der Satz der Zeile nennt beide Positionen und den Grund (automatisch, Position in JTL ersetzt).
  // Das Ereignis `nicht_umgehaengt` (Artwork bleibt an der alten Position, weil das Ziel eines hat) braucht hier keinen Zweig: das
  // Backend schreibt Alt- und Neuwert als null, die Freitextregel oben zeigt den Satz schon.
  if (satz && UMGEHAENGT_FELDER.has(e.feld ?? "") && betroffenesEreignis(e) === "umgehaengt") {
    return { art: "satz", text: satz };
  }
  // Altzeilen von ist_zahlung_beleg und bestellgruppe tragen eine UUID: der Satz der Zeile tritt an ihre Stelle.
  if (satz && [e.altWert, e.neuWert].some((wert) => wert !== null && UUID_MUSTER.test(wert))) {
    return { art: "satz", text: satz };
  }
  const alt = lesbar(e, e.altAnzeige);
  const neu = lesbar(e, e.neuAnzeige);
  // Alt gleich neu (am Rohwert gemessen, nicht an der gekuerzten Anzeige): nur die Kennung, ohne Pfeil.
  if (e.altWert !== null && e.altWert === e.neuWert) return { art: "satz", text: neu ?? alt ?? "" };
  return { art: "werte", alt, neu };
}
